import { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Info, Loader2, UploadCloud } from "lucide-react";
import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { useTran } from "@/hooks";
import { useEntryUIStore } from "../../hooks/useDataEntryStore";
import {
  parseBatchUploadFile,
  confirmBatchUpload,
} from "../../operations/batch-upload.ops";
import UploadForm from "./UploadForm";
import ParsePreview from "./ParsePreview";

const BatchUploadDialog = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const {
    downloadingTemplate,
    batchUploadOpen,
    setBatchUploadOpen,
    parsingFile,
    confirmingUpload,
    parseResult,
    setParseResult,
  } = useEntryUIStore();
  const fileInputRef = useRef(null);

  const resetSelectedFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(
    () => () => {
      resetSelectedFile();
      setBatchUploadOpen(false);
    },
    [setBatchUploadOpen],
  );

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) resetSelectedFile();
    setBatchUploadOpen(nextOpen);
  };

  const parseFirstFile = (file) => {
    if (file) parseBatchUploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    parseFirstFile(e.dataTransfer.files?.[0]);
  };

  const handleFileChange = (e) => {
    parseFirstFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const summary = parseResult?.summary;
  const uploadValidation = parseResult?.uploadValidation;
  const canConfirm = uploadValidation
    ? uploadValidation.canConfirm === true
    : summary?.totalValid > 0;

  const isDirty = !!parseResult;

  return (
    <AppDialog
      open={batchUploadOpen}
      onOpenChange={handleOpenChange}
      isDirty={isDirty}
      title={
        <span className="inline-flex items-center gap-2">
          {t("Batch Upload")}
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors">
                <Info className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              className="w-72 text-sm"
            >
              <p className="font-medium mb-2">{t("How to use Batch Upload")}</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>{t("Download the Excel template")}</li>
                <li>{t("Fill in your data following the column headers")}</li>
                <li>
                  {t("Use the dropdowns for Org, Framework, and Metric fields")}
                </li>
                <li>{t("Upload the completed file")}</li>
                <li>{t("Review the preview and confirm")}</li>
              </ol>
            </PopoverContent>
          </Popover>
        </span>
      }
      description={
        parseResult
          ? uploadValidation?.message ||
            t("Review parsed data before confirming")
          : t("Download the template, fill in your data, then upload it here")
      }
      icon={UploadCloud}
      size={parseResult ? "xl" : "lg"}
      footer={
        parseResult ? (
          <div className="flex items-center justify-between gap-x-2">
            <Button variant="outline" onClick={() => setParseResult(null)}>
              {t("Back")}
            </Button>
            <Can
              required={["esg_data_entry:create", "esg_data_entry:update"]}
              mode="any"
              userPermissions={permissions}
            >
              <Button
                onClick={confirmBatchUpload}
                disabled={!canConfirm || confirmingUpload}
              >
                {confirmingUpload && (
                  <Loader2 size={14} className="animate-spin" />
                )}
                {confirmingUpload
                  ? t("Uploading...")
                  : t("Confirm {{count}} entries", {
                      count: summary?.totalValid || 0,
                    })}
              </Button>
            </Can>
          </div>
        ) : null
      }
    >
      {parseResult ? (
        <ParsePreview result={parseResult} />
      ) : (
        <UploadForm
          downloadingTemplate={downloadingTemplate}
          parsingFile={parsingFile}
          fileInputRef={fileInputRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onFileChange={handleFileChange}
        />
      )}
    </AppDialog>
  );
});

export default BatchUploadDialog;
